import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { withTheme } from 'styled-components';
import Loader from '@rsos/components/capstone/Loader';
import { Absolute, Box } from '@rsos/components/capstone/base';
import GoogleMap from '@rsos/components/capstone/common/Map/GoogleMap';
import { mapOptions } from '@rsos/components/capstone/common/Map/mapDefaults';
import { mapStyles } from '@rsos/components/capstone/common/Map/mapStyles';
import useIsGoogleMapsFeatureFlagEnhanced from '@rsos/hooks/useIsGoogleMapsFeatureFlagEnhanced';
import { getGoogleMapsApiKey } from '@rsos/utils/metaTags';
import CallsInPolygonGL from './CallsInPolygonGL';
import CrgLocationMarkers from './CrgLocationMarkers';
import MapUnavailable from './MapUnavailable';

const apiKey = getGoogleMapsApiKey();

const createOverlayElement = () => {
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.display = 'inline-block';
  return el;
};

/**
 * Component responsible for rendering a PNG on top of Google Maps within the CRG expanded map card
 * @param {Object} activeCrgObject
 * @param {Object} activeCrgObject.spatialObject - the current active spatial object
 * @param {Object} activeCrgObject.activeFloor - data for the current active floor
 * @param {Object} activeCrgObject.deeplink - deeplink data for the floors
 * @param {Boolean} activeCrgObject.deeplink.loading - loading state
 * @param {Object} activeCrgObject.deeplink.error
 * @param {Object} activeCrgObject.deeplink.data - data pertaining to the CRG building floors
 */
export const GeoTiffMapCard = withTheme(
  ({ activeCrgObject, removedID, selectedFloor, ...props }) => {
    const [map, setMap] = useState(null);
    const [el, setEl] = useState(null);
    const [customPopup, setCustomPopup] = useState(null);
    // On first load of the image needs to be fetched prior to rendering, otherwise the image dimensions are 0 when calculating how much to shift it by
    const [deeplinkImgLoaded, setDeeplinkImgLoaded] = useState(false);
    const [deeplinkImgHasError, setDeeplinkImgHasError] = useState(false);
    const theme = props.theme;
    const themeName = theme.colors.name;

    const rspDeckGL = useIsGoogleMapsFeatureFlagEnhanced();

    const googleMapOptions = {
      ...mapOptions,
      ...(activeCrgObject?.spatialObject?.center?.coordinates
        ? {
            center: {
              lat: activeCrgObject.spatialObject.center.coordinates[1],
              lng: activeCrgObject.spatialObject.center.coordinates[0],
            },
          }
        : {}),
      disableDefaultUI: true, // Remove controls
      // Note: the settings below can also affect the main map since this component is inside of an Overlay from Google Maps
      disableDoubleClickZoom: true, // Disable zoom by double clicking
      scrollwheel: true, // Enable scroll to zoom
      zoomControl: true, // Only enable zoom controls
    };

    const handleMapReady = mapInstance => {
      if (mapInstance) {
        setMap(mapInstance);
      }
    };

    const selectedFloorUri = selectedFloor?.data?.link_uri;
    const selectedFloorDeeplinkUrl =
      activeCrgObject?.deeplink?.data?.floors?.find(
        floor => floor.link_uri === selectedFloorUri,
      )?.url;

    useEffect(() => {
      if (selectedFloorDeeplinkUrl) {
        setDeeplinkImgLoaded(false);
        const img = new Image();
        img.onload = () => {
          setDeeplinkImgLoaded(true);
        };
        img.src = selectedFloorDeeplinkUrl;
        img.onerror = () => {
          setDeeplinkImgHasError(true);
        };
      }
    }, [selectedFloorDeeplinkUrl]);

    // Handle the PNG
    useEffect(() => {
      if (deeplinkImgLoaded && selectedFloorDeeplinkUrl && map) {
        (async () => {
          const { default: CustomOverlay, BOUNDS } = await import(
            '@rsos/components/capstone/common/Map/CustomOverlay'
          );

          const element = createOverlayElement();

          setEl(element);
          const bounds = map.getBounds();
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const lat = sw.lat();
          const lng = ne.lng();

          const mapAreaCoordinates =
            selectedFloor?.data?.map_area?.features[0]?.geometry
              ?.coordinates[0];
          /**
           * Get the most northwestern and southeastern coordinates for
           * positioning the image
           * @param {Array} mapAreaCoordinates - the spatial object's geojson
           * coordinates
           */
          const getMapPosition = mapAreaCoordinates => {
            const latitudes = mapAreaCoordinates.map(
              coordinates => coordinates[1],
            );
            const longitudes = mapAreaCoordinates.map(
              coordinates => coordinates[0],
            );
            return {
              northwest: {
                lat: Math.max(...latitudes),
                lng: Math.min(...longitudes),
              },
              southeast: {
                lat: Math.min(...latitudes),
                lng: Math.max(...longitudes),
              },
            };
          };
          const popup = new CustomOverlay({
            position: new window.google.maps.LatLng(lat, lng),
            content: element,
            options: {
              contentPosition: BOUNDS,
              contentMapPosition: getMapPosition(mapAreaCoordinates),
              paneLevel: rspDeckGL ? 0 : 2,
              // Using deck.gl for the map markers, the image needs to be in the
              // lowest pane or prepended to the next lowest pane instead of
              // being appended to the paneLevel
            },
          });
          popup.setMap(map);
          // Remove the current card that's open if there is one
          if (customPopup) {
            customPopup.setMap(null);
          }
          setCustomPopup(popup);
        })();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, deeplinkImgLoaded]);

    useEffect(() => {
      if (map && Object.keys(selectedFloor).length) {
        const mapAreaCoordinates =
          selectedFloor?.data?.map_area?.features[0]?.geometry?.coordinates[0];
        const bounds = new window.google.maps.LatLngBounds();
        mapAreaCoordinates.forEach(coordinates => {
          bounds.extend({
            lat: parseFloat(coordinates[1]),
            lng: parseFloat(coordinates[0]),
          });
          map.fitBounds(bounds);
        });

        // Restrict the user from zooming or panning beyond the spatial object
        map.setOptions({
          restriction: {
            latLngBounds: bounds,
          },
        });
      }
    }, [map, selectedFloor]);

    // Update Map with the associated theme's Map styling
    useEffect(() => {
      if (map) {
        map.setOptions({
          styles: mapStyles[themeName],
        });
      }
    }, [themeName, map]);

    // Set state back to false so the handling for the deeplink image position
    // is handled appropriately by pre-loading it
    useEffect(() => {
      return () => {
        setDeeplinkImgLoaded(false);
      };
    }, []);

    const hasError =
      activeCrgObject.deeplink.error !== null || deeplinkImgHasError;

    return (
      <>
        <GoogleMap
          apiKey={apiKey}
          mapOptions={googleMapOptions}
          onReady={handleMapReady}
        />
        {map && (
          <>
            {rspDeckGL ? (
              <CallsInPolygonGL activeCrgObject={activeCrgObject} map={map} />
            ) : (
              <CrgLocationMarkers
                activeCrgObject={activeCrgObject}
                map={map}
                removedID={removedID}
              />
            )}
          </>
        )}
        {hasError || !Object.keys(selectedFloor).length ? (
          <MapUnavailable />
        ) : activeCrgObject.deeplink.loading ||
          !Object.keys(activeCrgObject.deeplink.data).length ||
          !deeplinkImgLoaded ? (
          // TODO: update Absolute to accept a height prop
          <Absolute width={'100%'} style={{ height: '100%' }}>
            <Box
              height={'100%'}
              align="center"
              justify="center"
              bg="foreground"
            >
              <Loader />
            </Box>
          </Absolute>
        ) : (
          <>
            {customPopup && selectedFloorDeeplinkUrl && el && (
              <>
                {ReactDOM.createPortal(
                  <>
                    {/* The image needs to be the first child for the CustomOverlay*/}
                    <img
                      src={selectedFloorDeeplinkUrl}
                      alt={activeCrgObject.activeFloor.floor_name}
                    />
                  </>,
                  el,
                )}
              </>
            )}
          </>
        )}
      </>
    );
  },
);
